import React, { useEffect, useState, useCallback, useRef } from "react";
import "./PreviewDrawerContent.scss";
import useApi from "../../../../../hooks/useApi";
import API from "../../../../../services/rest/api";

import { toast } from "react-toastify";
import {

  setPreAssessments,
 
} from "../../../../../store/slices/GoalSlice";
import Table from "../../../../../atoms/Table/Table";
import Loader from "../../../../../atoms/Loader";
import Button from "../../../../../atoms/Button/Button";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../../store/store";
import { Select } from "antd";
import { getEncryptedBody, getDecryptedData } from "../../../../../utils/common";

interface PreviewDrawerContentProps {
  
  patientId: string;
  activeTab: string;
  onClose?: () => void;
 
  onSaveStateChange?: (hasChanges: boolean, handleSave: () => void, isLoading: boolean) => void;
  
  onDataRefresh?: () => void;
}

const PreviewDrawerContent: React.FC<PreviewDrawerContentProps> = ({
  patientId,
  activeTab,
  onClose,
  onSaveStateChange,
  onDataRefresh,
}) => {
  const {
    mutate: fetchPreAssessments,
    response: preAssessmentsResponse,
    error: preAssessmentsError,
    isLoading: isPreAssessmentsLoading,
  } = useApi(API.GetPreviewAssessments);

  
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);

  const patientInfo = JSON.parse(localStorage.getItem("PatientInfo") || "{}");
  const healthCoachRole = useSelector((state: RootState) => state.healthCoach.healthCoachDetails?.data?.role);
 
  

  const dispatch = useDispatch();
  const { preAssessments } = useSelector((state: RootState) => state.goals);


  
  const onSaveStateChangeRef = useRef(onSaveStateChange);
  onSaveStateChangeRef.current = onSaveStateChange;

 
  const [editableRows, setEditableRows] = useState<Set<string>>(new Set());
  
  const [editedAnswers, setEditedAnswers] = useState<Record<string, { original: string; current: string }>>({});

  useEffect(() => {
    if (preAssessmentsResponse?.code?.toString() === "1") {
      dispatch(
        setPreAssessments({ preAssessments: preAssessmentsResponse?.data })
      );
    } else if (preAssessmentsResponse?.code?.toString() === "0") {
      toast.error(preAssessmentsResponse?.message || "Failed to fetch goals");
    }
  }, [preAssessmentsResponse, preAssessmentsError]);

  useEffect(() => {
   
      fetchPreAssessments({
        patient_id: patientId,
        disease: patientInfo?.disease || "",
        health_coach_role: healthCoachRole || "",
       
      });
    
  }, [healthCoachRole,patientId,patientInfo?.disease]);


 
  const handleUpdateAPI = async (payload: any) => {
    setIsUpdateLoading(true);
    try {
      const encryptedPayload = getEncryptedBody(payload);
      const response = await API.UpdatePreAssessmentQA(encryptedPayload);
      
    
      const decryptedData = getDecryptedData(response);
      const parsedResponse = JSON.parse(decryptedData);
      
      if (parsedResponse?.code?.toString() === "1") {
        toast.success(parsedResponse?.msg || "Assessment updated successfully!");
       
        setEditedAnswers({});
        setEditableRows(new Set());
       
        onDataRefresh && onDataRefresh();
      
        onClose && onClose();
      } else {
        toast.error(parsedResponse?.msg || "Failed to update assessment");
      }
    } catch (error) {
     
      toast.error("Error updating assessment. Please try again.");
    } finally {
      setIsUpdateLoading(false);
    }
  };

  const handleEdit = (rowData: any) => {
    const rowId = rowData.id || rowData.question;
    
    setEditableRows(prev => new Set(prev).add(rowId));
   
    setEditedAnswers(prev => ({
      ...prev,
      [rowId]: {
        original: rowData.answer || "",
        current: rowData.answer || ""
      }
    }));
  };

  const handleAnswerChange = (rowId: string, value: string) => {
    setEditedAnswers(prev => ({
      ...prev,
      [rowId]: {
        ...prev[rowId],
        current: value
      }
    }));
  };

  const handleCancel = (rowData: any) => {
    const rowId = rowData.id || rowData.question;
    setEditableRows(prev => {
      const newSet = new Set(prev);
      newSet.delete(rowId);
      return newSet;
    });
  
    setEditedAnswers(prev => {
      const newAnswers = { ...prev };
      delete newAnswers[rowId];
      return newAnswers;
    });
  };


  const hasActualChanges = useCallback(() => {
    return Object.values(editedAnswers).some(
      ({ original, current }) => original !== current
    );
  }, [editedAnswers]);

 
  const handleSaveAll = useCallback(() => {
    if (isUpdateLoading) return;
    
    const qa_obj = [];
    
   
    for (const [rowId, { original, current }] of Object.entries(editedAnswers)) {
    
      if (original === current) continue;
      
      const assessment = preAssessments.find((item: any) => 
        (item.id || item.question) === rowId
      );
      
      if (assessment) {
        const selectedOption = assessment.options?.find((option: any) => 
          option.label === current
        );
        
        if (selectedOption) {
          const qaItem = {
            therapy_condition_section_name: assessment.therapySectionName || "",
            questionId: assessment.questionId,
            selected_answer_id: selectedOption.id,
            therapy_condition_section_id: assessment.therapySectionConditionId,
            tag: assessment.tag || activeTab,
          };
          qa_obj.push(qaItem);
        }
      }
    }
    
    if (qa_obj.length === 0) {
      toast.warning("No changes to save");
      return;
    }
    
    const payload = {
      patient_id: patientId,
      disease: patientInfo?.disease || "",
      qa_obj: qa_obj
    };
    
    handleUpdateAPI(payload);
  }, [editedAnswers, preAssessments, patientId, isUpdateLoading]);


  const getSelectOptions = (questionData: any) => {
   
    if (questionData?.options && Array.isArray(questionData.options) && questionData.options.length > 0) {
      const mappedOptions = questionData.options.map((option: any) => ({
        value: option.label,
        label: option.label
      }));
      return mappedOptions;
    }

    return [];
  };

 
  useEffect(() => {
    if (!onSaveStateChangeRef.current) return;
    
    const hasChanges = hasActualChanges();
    onSaveStateChangeRef.current(hasChanges, handleSaveAll, isUpdateLoading);
  }, [hasActualChanges, handleSaveAll, isUpdateLoading]);

  return (
    <div className="preview-columns">
      {isPreAssessmentsLoading || isUpdateLoading ? (
        <Loader noBackground={true} />
      ) : (
        <Table
          data={preAssessments}
          columns={[
            {
              label: "Question",
              accessor: "question",
            },
            {
              label: "Answer",
              accessor: "answer",
              render: (value: any, row: any, rowIndex: number) => {
                const rowId = row.id || row.question;
                const isEditing = editableRows.has(rowId);
                
                if (isEditing) {
                  return (
                    <div className="answer-dropdown-container">
                      <Select
                        value={editedAnswers[rowId]?.current || ""}
                        onChange={(value) => handleAnswerChange(rowId, value)}
                        style={{ width: '100%' }}
                        placeholder="Select an answer"
                        options={getSelectOptions(row)}
                      />
                    </div>
                  );
                }
                
                return value;
              },
            },
           
            ...(activeTab !== "diagnostics" && preAssessments?.some((row: any) => row.isEditable) ? [{
              label: "Actions",
              accessor: "actions",
              render: (value: any, row: any, rowIndex: number) => {
                const rowId = row.id || row.question;
                const isEditing = editableRows.has(rowId);
                
                if (isEditing) {
                  return (
                    <div className="action-buttons">
                      <Button
                        variant="outlined"
                        className="cancel-button small-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCancel(row);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  );
                }
                
                
                return (
                  <Button
                    className="edit-button small-button"
                    disabled={row.isEditable === false}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(row);
                    }}
                  >
                    Edit
                  </Button>
                );
              },
            }] : []),
          ]}
          border={true}
        />
      )}
    </div>
  );
};

export default PreviewDrawerContent;
